import { notificacion, notificacionError } from "@/components/Alerts";
import { Disclosure } from "@/components/Disclosure";
import { Loading, QueryError } from "@/components/Loading/Loading";
import { FiltroMarca } from "@/pages/3_Produccion/CategorizarArticulos/FiltroMarca";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import CreatableSelect from "react-select/creatable";
import { ComunicacionAjax } from "../../../ts/api/comunicacion";
import "./CategorizarArticulos.scss";
import { useOpcionesCategoria, useMapeos, type OpcionesCategorias, type MapeoArticuloRaw } from "../../../hooks/Api/Categorias";
import { useArticulos, type Articulo } from "@/hooks/Api/Articulos";


export function CategorizarArticulosPage() {
	const [idFamilia, setIdFamilia] = useState("8");
	const [categoria, setCategoria] = useState<string | undefined>(undefined);
	const queryArticulos = useArticulos();
	const queryCategorias = useOpcionesCategoria();
	const queryMapeos = useMapeos();

	const isPending = queryArticulos.isPending || queryCategorias.isPending || queryMapeos.isPending;

	return (
		<div id="content" className="content categorizarArticulos" >
			<title>Almacen | Categorizar articulos</title>

			<Disclosure style={{ marginBottom: 20 }}>
				<h4 className="panel-title panel-heading senalar">
					Marca
				</h4>
				<FiltroMarca familia={idFamilia} setFamilia={setIdFamilia} categoria={categoria} setCategoria={setCategoria} />
			</Disclosure>
			<div>
				{isPending == true && <Loading />}

				{queryMapeos.isError == true && <QueryError error={queryMapeos.error} fallback="Error al cargar los mapeos" />}
				{queryArticulos.isError == true && <QueryError error={queryArticulos.error} fallback="Error al cargar los articulos" />}
				{queryCategorias.isError == true && <QueryError error={queryCategorias.error} fallback="Error al cargar las categorías" />}
				{queryArticulos.isSuccess == true && queryCategorias.isSuccess == true && queryMapeos.isSuccess == true &&
					<TablaCategorizarArticulos
						idFamilia={idFamilia}
						categoria={categoria}
						articulos={queryArticulos.data}
						mapeos={queryMapeos.data}
						categorias={queryCategorias.data}
					/>}
			</div>
		</div>
	);
}

function TablaCategorizarArticulos(props: { readonly idFamilia: string, readonly categoria: string | undefined, readonly articulos: Articulo[], readonly mapeos: MapeoArticuloRaw[], readonly categorias: OpcionesCategorias; }) {
	const queryClient = useQueryClient();
	const { articulos, categorias } = props;
	const [search, setSearch] = useState<string>("");


	const filas = useMemo(() => {
		const mapaMapeos = new Map(props.mapeos.map(articulo => [articulo.idArticulo, articulo]));
		const mapeos = new Array<MapeoArticulo>();

		for (const articulo of articulos) {
			if (articulo.idFamilia != Number(props.idFamilia)) continue; // Solo procesamos artículos de la familia seleccionada

			const mapeoExistente = mapaMapeos.get(articulo.idArticulo);
			if (props.categoria != null) {
				if (mapeoExistente?.categoria !== props.categoria) {
					const sinCategoria = mapeoExistente?.categoria == null || mapeoExistente.categoria == "";
					if (!(sinCategoria && props.categoria == "")) // Si el artículo no tiene categoría asignada y filtramos por una categoría vacía, sí lo mostramos
						continue; // Si el mapeo existente tiene una categoría diferente a la seleccionada, lo saltamos
				}
			}

			mapeos.push({
				idArticulo: articulo.idArticulo,
				refArticulo: articulo.referencia,
				descArticulo: articulo.nombreArticulo,

				categoria: mapeoExistente?.categoria ?? "",
				gama: mapeoExistente?.gama ?? "",
				tipo: mapeoExistente?.tipo ?? "",
				subtipo: mapeoExistente?.subtipo ?? "",
				tamano: mapeoExistente?.tamano ?? "",
			});
		}


		return mapeos;
	}, [articulos, props.categoria, props.idFamilia, props.mapeos]);
	const [dirtyFilas, setDirtyFilas] = useState(filas);

	//#region Paginación
	const [paginaActual, setPaginaActual] = useState(1);
	const [articulosConError, setArticulosConError] = useState<Set<number>>(new Set());
	const elementosPorPagina = 12;
	useEffect(() => {
		setDirtyFilas(filas);
		setArticulosConError(new Set());
		setPaginaActual(1);
	}, [filas, props.idFamilia]);
	const articulosPaginados = useMemo(() => {
		const indexUltimo = paginaActual * elementosPorPagina;
		const indexPrimero = indexUltimo - elementosPorPagina;
		return dirtyFilas
			.sort((a, b) => a.descArticulo.localeCompare(b.descArticulo))
			.filter(mapeo => {
				const textoBusqueda = search.toLowerCase();
				if (textoBusqueda === "") return true;
				if (mapeo.refArticulo.toLowerCase().includes(textoBusqueda)) return true;
				if (mapeo.descArticulo.toLowerCase().includes(textoBusqueda)) return true;
				return false;
			})
			.slice(indexPrimero, indexUltimo);
	}, [dirtyFilas, paginaActual, search]);
	const totalPaginas = Math.ceil(dirtyFilas.length / elementosPorPagina);
	//#endregion


	const opciones = useMemo(() => {
		return {
			categoria: mapToOptions(categorias.categorias),
			gama: mapToOptions(categorias.gamas),
			tipo: mapToOptions(categorias.tipos),
			subtipo: mapToOptions(categorias.subtipos),
			tamano: mapToOptions(categorias.tamanos),
		};
	}, [categorias]);

	const updateFila = useCallback((newMapeoArticulo: MapeoArticulo) => {
		setDirtyFilas((oldFilas) => {
			const index = oldFilas.findIndex(mapeo => mapeo.idArticulo === newMapeoArticulo.idArticulo);
			const newFilas = [...oldFilas];
			newFilas[index] = newMapeoArticulo;
			return newFilas;
		});

		if (esIncompleto(newMapeoArticulo) && !esVacio(newMapeoArticulo)) {
			setArticulosConError(prevErrores => {
				const nuevosErrores = new Set(prevErrores);
				nuevosErrores.add(newMapeoArticulo.idArticulo);
				return nuevosErrores;
			});
		} else {
			setArticulosConError(prevErrores => {
				const nuevosErrores = new Set(prevErrores);
				nuevosErrores.delete(newMapeoArticulo.idArticulo);
				return nuevosErrores;
			});
		}
	}, []);
	const guardarMapeos = useCallback(() => {
		const enError = dirtyFilas.some(mapeo => esIncompleto(mapeo) && !esVacio(mapeo));
		if (enError) {
			const nuevosErrores = dirtyFilas.filter(mapeo => esIncompleto(mapeo) && !esVacio(mapeo)).map(mapeo => mapeo.idArticulo);
			setArticulosConError(new Set(nuevosErrores));
			notificacionError("Faltan componentes. Las filas incompletas están marcadas en rojo.");
			return;
		}

		const mapeoArticulosParaEnviar: unknown[] = [];
		for (const mapeo of dirtyFilas) {
			if (esVacio(mapeo)) continue; // No enviar filas vacías

			mapeoArticulosParaEnviar.push({
				idArticulo: mapeo.idArticulo,
				categoria: mapeo.categoria,
				gama: mapeo.gama,
				tipo: mapeo.tipo,
				subtipo: mapeo.subtipo,
				tamano: mapeo.tamano,
			});
		}



		ComunicacionAjax.sendAjaxRequest("POST", "/api/v1/mapeoArticulos", {
			idFamilia: props.idFamilia,
			mapeoArticulos: mapeoArticulosParaEnviar,
		}).then(() => {
			void queryClient.invalidateQueries({ queryKey: ["mapeoArticulos"] });
			void queryClient.invalidateQueries({ queryKey: ["opcionesCategorizacion"] });
			notificacion("Mapeos guardados correctamente");
			setArticulosConError(new Set());
		}).catch((err: unknown) => {
			console.error(err);
			notificacionError(err instanceof Error ? `Error al guardar: ${err.message}` : "Error al guardar los mapeos");
		});
	}, [dirtyFilas, props.idFamilia, queryClient]);

	return (
		<div>
			<div className="headerArticulos">
				<h3>Listado de Artículos ({filas.length})</h3>

				<input type="search" placeholder="Buscar por referencia o descripción..." onChange={(e) => setSearch(e.target.value)} value={search} className="inputsForm" style={{ width: "40ch" }} />
			</div>

			<div className="contenedor-tabla">
				<table className="tabla-listado" style={{ width: "100%", borderCollapse: "collapse", marginTop: "10px" }}>
					<thead>
						<tr style={{ textAlign: "left", borderBottom: "2px solid #ddd" }}>
							<th style={{ padding: "10px" }}>Artículo</th>
							<th style={{ padding: "10px", width: "15%" }}>Categoria</th>
							<th style={{ padding: "10px", width: "15%" }}>Tipo</th>
							<th style={{ padding: "10px", width: "15%" }}>Subtipo</th>
							<th style={{ padding: "10px", width: "15%" }}>Gama</th>
							<th style={{ padding: "10px", width: "15%" }}>Tamaño</th>
						</tr>
					</thead>
					<tbody>
						{articulosPaginados.map((articulo) => (
							<FilaArticulo
								key={articulo.idArticulo}
								articulo={articulo}
								tieneError={articulosConError.has(articulo.idArticulo)}
								opcionesCategoria={opciones.categoria}
								opcionesGama={opciones.gama}
								opcionesTipo={opciones.tipo}
								opcionesSubtipo={opciones.subtipo}
								opcionesTamano={opciones.tamano}
								updateFila={updateFila}
							/>
						))}
						{filas.length === 0 && (
							<tr><td colSpan={5} style={{ padding: "20px", textAlign: "center" }}>No hay artículos en esta familia.</td></tr>
						)}
					</tbody>
				</table>
			</div>
			<div className="acciones-mapeo" style={{ margin: "10px 0" }}>
				{totalPaginas > 1 && (
					<div style={{ display: "flex", gap: 10, alignItems: "center" }}>
						<button type="button" className="btnSecondary" onClick={() => setPaginaActual(p => Math.max(1, p - 1))} disabled={paginaActual === 1}> Anterior </button>
						<span>Página {paginaActual} de {totalPaginas}</span>
						<button type="button" className="btnSecondary" onClick={() => setPaginaActual(p => Math.min(totalPaginas, p + 1))} disabled={paginaActual === totalPaginas}> Siguiente </button>
					</div>
				)}
				<button className="guardar btnPrimary" onClick={guardarMapeos} type="button">Guardar cambios</button>
			</div>
		</div>
	);
}
const mapToOptions = (arr: string[] = []) => arr.filter(Boolean).map(item => ({ label: item, value: item }));
const esVacio = (mapeo: MapeoArticulo) => mapeo.gama == "" && mapeo.tipo == "" && mapeo.subtipo == "" && mapeo.tamano == "" && mapeo.categoria == "";
const esIncompleto = (newMapeoArticulo: MapeoArticulo) => newMapeoArticulo.gama == "" || newMapeoArticulo.tipo == "" || newMapeoArticulo.subtipo == "" || newMapeoArticulo.tamano == "" || newMapeoArticulo.categoria == "";

interface PropsFilaArticulo {
	readonly articulo: MapeoArticulo;
	readonly tieneError: boolean;
	readonly opcionesCategoria: Array<{ label: string; value: string; }>;
	readonly opcionesGama: Array<{ label: string; value: string; }>;
	readonly opcionesTipo: Array<{ label: string; value: string; }>;
	readonly opcionesSubtipo: Array<{ label: string; value: string; }>;
	readonly opcionesTamano: Array<{ label: string; value: string; }>;
	readonly updateFila: (newMapeoArticulo: MapeoArticulo) => void;
}
function FilaArticulo({ articulo, tieneError, opcionesCategoria, opcionesGama, opcionesTipo, opcionesSubtipo, opcionesTamano, updateFila }: PropsFilaArticulo) {
	const onChange = (field: keyof Omit<MapeoArticulo, "idArticulo" | "refArticulo" | "descArticulo">, value: string) => {
		updateFila({ ...articulo, [field]: value });
	};

	return (
		<tr className={tieneError ? "fila-error" : ""} style={{ borderBottom: "1px solid #eee" }}>
			<td style={{ padding: "10px", verticalAlign: "middle" }}>
				<div style={{ fontWeight: "bold" }}>{articulo.descArticulo}</div>
				<div style={{ color: "#666", fontSize: "0.85em" }}>{articulo.refArticulo}</div>
			</td>
			<td style={{ padding: "5px" }}>
				<CreatableSelect
					isClearable
					options={opcionesCategoria}
					value={articulo.categoria ? { label: articulo.categoria, value: articulo.categoria } : null}
					onChange={(opt) => onChange("categoria", opt?.value ?? "")}
					menuPosition="fixed" className="custom-select" placeholder="Categoría"
					formatCreateLabel={(val) => `Crear "${val}"`}
				/>
			</td>
			<td style={{ padding: "5px" }}>
				<CreatableSelect
					isClearable
					options={opcionesTipo}
					value={articulo.tipo ? { label: articulo.tipo, value: articulo.tipo } : null}
					onChange={(opt) => onChange("tipo", opt?.value ?? "")}
					menuPosition="fixed" className="custom-select" placeholder="Tipo"
					formatCreateLabel={(val) => `Crear "${val}"`}
				/>
			</td>
			<td style={{ padding: "5px" }}>
				<CreatableSelect
					isClearable
					options={opcionesSubtipo}
					value={articulo.subtipo ? { label: articulo.subtipo, value: articulo.subtipo } : null}
					onChange={(opt) => onChange("subtipo", opt?.value ?? "")}
					menuPosition="fixed" className="custom-select" placeholder="Subtipo"
					formatCreateLabel={(val) => `Crear "${val}"`}
				/>
			</td>
			<td style={{ padding: "5px" }}>
				<CreatableSelect
					isClearable
					options={opcionesGama}
					value={articulo.gama ? { label: articulo.gama, value: articulo.gama } : null}
					onChange={(opt) => onChange("gama", opt?.value ?? "")}
					menuPosition="fixed" className="custom-select" placeholder="Gama"
					formatCreateLabel={(val) => `Crear "${val}"`}
				/>
			</td>
			<td style={{ padding: "5px" }}>
				<CreatableSelect
					isClearable
					options={opcionesTamano}
					value={articulo.tamano ? { label: articulo.tamano, value: articulo.tamano } : null}
					onChange={(opt) => onChange("tamano", opt?.value ?? "")}
					menuPosition="fixed" className="custom-select" placeholder="Tamaño"
					formatCreateLabel={(val) => `Crear "${val}"`}
				/>
			</td>
		</tr>
	);
}




interface MapeoArticulo {
	idArticulo: number;
	refArticulo: string;
	descArticulo: string;

	categoria: string;
	gama: string;
	tipo: string;
	subtipo: string;
	tamano: string;
}
